import { useCallback, useState } from "react";
import { useMapUrl } from "../../hooks/use-map-url";
import { updateUserZipCode } from "../../services/userProfileService";
import { debugLogError } from "../../utils/debug-log-error";

interface ZipCodeUpdateFormProps {
  userUid: string | null;
  zipCode: string | null;
  onError: (error: Error) => void;
  onClearError: () => void;
}

const ZipCodeUpdateForm: React.FC<ZipCodeUpdateFormProps> = ({
  userUid,
  zipCode,
  onError,
  onClearError,
}) => {
  const [editingZipCode, setEditingZipCode] = useState(false);
  const [newZipCode, setNewZipCode] = useState("");
  const { mapUrl } = useMapUrl(zipCode, "map-center");

  const handleZipCodeEditToggle = useCallback(() => {
    setEditingZipCode(!editingZipCode);
    if (!editingZipCode && zipCode) {
      setNewZipCode(zipCode);
    }
  }, [editingZipCode, zipCode]);

  const handleZipCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewZipCode(event.target.value);
  };

  const handleSaveZipCode = useCallback(async () => {
    if (!userUid || !newZipCode) {
      return;
    }

    if (!newZipCode.trim()) {
      onError(new Error("Zip code cannot be empty."));
      return;
    }

    onClearError();
    try {
      await updateUserZipCode(userUid, newZipCode);
      setEditingZipCode(false);
    } catch (err) {
      debugLogError("ProfilePage::handleSaveZipCode", err);
      onError(new Error("Failed to update zip code. Please try again."));
    }
  }, [userUid, newZipCode, onError, onClearError]);

  return (
    <div>
      <div className="form-group zip-code-section">
        <div className="form-label">Zip Code</div>
        {editingZipCode ? (
          <>
            <input
              type="text"
              className="form-control"
              value={newZipCode}
              onChange={handleZipCodeChange}
            />
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSaveZipCode}>
                Save
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleZipCodeEditToggle}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={zipCode || ""}
                readOnly
                placeholder="No zip code set"
              />
              <button
                className="btn btn-outline-dark btn-sm"
                type="button"
                onClick={handleZipCodeEditToggle}
                aria-label="Edit zip code"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
            </div>
            <div className="form-caption">
              Set the zip code to wherever you're trying to play pool.
            </div>
          </>
        )}
      </div>
      {mapUrl && (
        <div className="zip-code-map-container">
          <img src={mapUrl} alt={`Map for zip code: ${zipCode}`} />
        </div>
      )}
    </div>
  );
};
export default ZipCodeUpdateForm;
